import classNames from 'classnames';
import { object, shape } from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { SkjemaSporsmal } from '../../ahv-schema';
import connect from '../helper/connect';
import text from '../../constants/text';
import ErrorMessage from '../errormessage';
import { isRequiredQuestion } from '../helper/component-helper';
import DropDown from '../../drop-down';

// Check if the date is not earlier than a given date from backend, format yyyy-mm-dd
function belowMinDate(minDate, value) {
    if (minDate && value) {
        return minDate > value;
    }
    return false;
}

const MONTH_OPTIONS = [
    { value: 0, text: 'Januar' },
    { value: 1, text: 'Februar' },
    { value: 2, text: 'Mars' },
    { value: 3, text: 'April' },
    { value: 4, text: 'Mai' },
    { value: 5, text: 'Juni' },
    { value: 6, text: 'Juli' },
    { value: 7, text: 'August' },
    { value: 8, text: 'September' },
    { value: 9, text: 'Oktober' },
    { value: 10, text: 'November' },
    { value: 11, text: 'Desember' },
];

const NUMBER_OF_YEARS_TO_SHOW_FROM_CURRENT_YEAR = 101;

function DateQuestionType({ question: { definisjon, instruks, minDate, sporsmalstekst, utfyllingsregel }, context }) {
    const [month, setMonth] = useState(undefined);
    const [year, setYear] = useState(undefined);
    const [monthAndYearTouched, setMonthAndYearTouched] = useState(false);
    const [yearOptions, setYearOptions] = useState([]);

    useEffect(() => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let i = currentYear; i > currentYear - NUMBER_OF_YEARS_TO_SHOW_FROM_CURRENT_YEAR; i -= 1) {
            years.push({
                value: i,
                text: `${i}`,
            });
        }
        setYearOptions(years);
    }, []);

    const hasInvalidValue = context.hasError(definisjon);

    const onChange = useCallback(
        (changedMonth, changedYear) => {
            let date;
            if (changedMonth !== undefined) {
                if (year !== undefined) {
                    date = new Date(year, changedMonth, 1);
                }
            } else if (changedYear !== undefined) {
                if (month !== undefined) {
                    date = new Date(changedYear, month, 1);
                }
            }

            if (date) {
                setMonthAndYearTouched(true);
                date = format(date, 'yyyy-MM-dd');
                context.onChange(definisjon, { value: date, error: belowMinDate(minDate, date) });
            } else if (monthAndYearTouched) {
                context.updateValidation(definisjon, true, true);
            } else {
                context.updateValidation(definisjon, true, false);
            }
        },
        [context, year, month, definisjon, monthAndYearTouched, minDate]
    );

    const removeMonth = useCallback(() => {
        setMonth(undefined);
        onChange();
    }, [onChange]);

    const removeYear = useCallback(() => {
        setYear(undefined);
        onChange();
    }, [onChange]);

    const selectMonth = useCallback(
        (value) => {
            setMonth(value);
            onChange(value, undefined);
        },
        [onChange]
    );

    const selectYear = useCallback(
        (value) => {
            setYear(value);
            onChange(undefined, value);
        },
        [onChange]
    );

    return (
        <div className={classNames({ 'has-error error': hasInvalidValue })}>
            {sporsmalstekst !== undefined && (
                <h2 className="skjema-input-question">{`${sporsmalstekst} ${
                    !isRequiredQuestion(utfyllingsregel) ? text.TEXT_OPTIONAL : ''
                }`}</h2>
            )}
            <div className="date-month-and-year">
                <DropDown
                    className={classNames('form-control', { 'has-error error': hasInvalidValue })}
                    values={MONTH_OPTIONS}
                    selected={month}
                    errortext={null}
                    placeholder={text.MONTH_TEXT}
                    removeValue={removeMonth}
                    selectValue={selectMonth}
                    customDropDownWidthClass="drop-down-list-width-42-percent"
                />
                <DropDown
                    className={classNames('form-control', { 'has-error error': hasInvalidValue })}
                    values={yearOptions}
                    selected={year}
                    errortext={null}
                    placeholder={text.YEAR_TEXT}
                    removeValue={removeYear}
                    selectValue={selectYear}
                    customDropDownWidthClass="drop-down-list-width-42-percent"
                />
            </div>
            <ErrorMessage
                text={hasInvalidValue && isRequiredQuestion(utfyllingsregel) ? instruks || text.ERROR_DATE_TEXT : ''}
            />
        </div>
    );
}

DateQuestionType.propTypes = {
    question: shape(SkjemaSporsmal).isRequired,
    context: object,
};

export default connect(React.memo(DateQuestionType));
